import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import 'pdfjs-dist/build/pdf.worker.entry';

export default function InvoicePage() {
  const { jobId } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [includeMarkup, setIncludeMarkup] = useState(true);

  const fetchPDF = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/invoices/view/${jobId}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setLoading(false);
    } catch (err) {
      setError(`Error loading invoice: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPDF();
  }, [jobId]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `invoice-${jobId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleAmountChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setTotalAmount(value);
  };

  // Calculate final amount with markup
  const getFinalAmount = () => {
    return includeMarkup 
      ? (totalAmount * 1.2).toFixed(2) 
      : totalAmount.toFixed(2);
  };

  const toggleMarkup = () => {
    setIncludeMarkup(!includeMarkup);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">Loading invoice...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Invoice for Job ID: {jobId}</h1>
        <button 
          onClick={handleDownload}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Download PDF
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
        <div className="flex items-center mb-2">
          <h2 className="text-lg font-semibold">Total Price with Homeowner Markup</h2>
          <div className="ml-auto">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={includeMarkup}
                onChange={toggleMarkup}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2">Include 20% markup</span>
            </label>
          </div>
        </div>
        <div className="flex items-center">
          <input 
            type="number" 
            value={totalAmount} 
            onChange={handleAmountChange}
            className="w-1/2 p-2 border rounded mr-4"
            step="0.01"
            placeholder="Enter base amount"
          />
          <div className="text-xl font-bold">
            Total: ${getFinalAmount()}
          </div>
        </div>
      </div>

      <div className="pdf-viewer overflow-auto">
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          className="flex flex-col items-center"
        >
          {numPages && Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={Math.min(window.innerWidth * 0.8, 800)}
              className="my-4 shadow-lg"
            />
          ))}
        </Document>
      </div>
    </div>
  );
}